import { apiClient } from '../lib/apiClient';
import { getCurrentUser } from '../lib/authUtils';

const PLACEHOLDER_IMAGE = '/placeholder.jpg';

/**
 * Get customer view ID from the current authenticated user
 * @returns {number|null} Customer view ID or null if not available
 */
const getCustomerViewId = () => {
  try {
    const user = getCurrentUser();

    if (user?.customerViewId) {
      return user.customerViewId;
    }
    // Also check nested customerAccount structure
    if (user?.customerAccount?.customerViewId) {
      return user.customerAccount.customerViewId;
    }
    return null;
  } catch (error) {
    console.warn('Error getting customer view ID:', error);
    return null;
  }
};

/**
 * Get price config ID from the current authenticated user
 * @returns {number|null} Price config ID or null if not available
 */
const getPriceConfigId = () => {
  try {
    const user = getCurrentUser();

    if (user?.priceConfigId) {
      return user.priceConfigId;
    }
    // Also check nested customerAccount structure
    if (user?.customerAccount?.priceConfigId) {
      return user.customerAccount.priceConfigId;
    }
    return null;
  } catch (error) {
    console.warn('Error getting price config ID:', error);
    return null;
  }
};

const slugify = (value) => {
  if (!value) return '';
  return value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const normalizeArrayResponse = (response) => {
  if (!response) return [];
  const { data } = response;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.result)) return data.result;
  return [];
};

const normalizeObjectResponse = (response) => {
  if (!response) return {};
  const { data } = response;
  if (Array.isArray(data)) return {};
  if (data && typeof data === 'object') return data;
  return {};
};

const ensureArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value == null) return [];
  return [value];
};

const toNumber = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^0-9.-]+/g, '');
    const parsed = Number.parseFloat(cleaned);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return 0;
};

const pickFirstNumber = (candidates, { allowZero = false } = {}) => {
  for (const candidate of candidates) {
    const numeric = toNumber(candidate);
    if (allowZero) {
      if (!Number.isNaN(numeric)) return numeric;
    } else if (numeric > 0) {
      return numeric;
    }
  }
  return 0;
};

const mapSpecification = (spec) => {
  if (!spec) return null;
  if (typeof spec === 'string') {
    const [label, ...rest] = spec.split(':');
    return {
      label: label?.trim() || 'Specification',
      value: rest.join(':').trim() || '',
    };
  }
  if (spec.label && spec.value) {
    return spec;
  }
  return {
    label: spec.name || spec.key || spec.label || 'Specification',
    value: spec.value || spec.data || '',
  };
};

const mapProduct = (product) => {
  if (!product) return null;

  const productId = product.product_id ?? product.productId ?? product.id ?? product._id ?? product.sku ?? product.code ?? null;
  const name = product.product_name ?? product.productName ?? product.name ?? product.title ?? 'Product';
  const slug = product.slug ?? product.productSlug ?? slugify(`${productId || ''}-${name}`);

  const primaryImageCandidate =
    product.product_img ??
    product.productImg ??
    product.proudimg_Url ??
    product.proudimg_url ??
    product.image ??
    product.imageUrl ??
    product.thumbnail ??
    product.mainImage ??
    product.productImage;

  const primaryImage = primaryImageCandidate || PLACEHOLDER_IMAGE;

  const gallery =
    ensureArray(
      product.productImages ??
        product.images ??
        product.imageGallery ??
        product.gallery ??
        (primaryImageCandidate ? [primaryImageCandidate] : []),
    ).filter(Boolean);

  const price = pickFirstNumber(
    [   product.viewPrice
      // product.promo_price,
      // product.promoPrice,
      // product.sale_price,
      // product.salesPrice,
      // product.salePrice,
      // product.sellingPrice,
      // product.price,
      // product.productPrice,
      // product.basePrice,
      // product.mrp_price,
      // product.mrpPrice,
      // product.mrp,
      // product.cost_price,
      // product.costPrice,
    ],
    { allowZero: false },
  );

  const originalPriceCandidate = pickFirstNumber(
    [
      product.mrp_price,
      product.mrpPrice,
      product.mrp,
      product.originalPrice,
      product.listPrice,
      product.basePrice,
      product.cost_price,
      product.costPrice,
    ],
    { allowZero: false },
  );

  const originalPrice = originalPriceCandidate > price ? originalPriceCandidate : null;

  const rating = pickFirstNumber(
    [product.rate, product.rating, product.averageRating, product.reviewScore],
    { allowZero: true },
  );
  const reviews = Math.max(
    0,
    Number.parseInt(
      product.reviews ?? product.reviewCount ?? product.totalReviews ?? product.reviewCountTotal ?? 0,
      10,
    ) || 0,
  );
  // Handle stock - can be a number, array of objects with Stock property, or various field names
  let stock = 0;
  if (Array.isArray(product.stock) && product.stock.length > 0) {
    // Sum up all Stock values from the array
    stock = product.stock.reduce((total, stockItem) => {
      const stockValue = stockItem?.Stock ?? stockItem?.stock ?? stockItem?.quantity ?? 0;
      return total + toNumber(stockValue);
    }, 0);
  } else {
    // Try various field names for single stock value
    stock = toNumber(
      product.stock ?? product.quantity ?? product.availableQuantity ?? product.stockQty ?? 0,
    ) || 0;
  }
  stock = Math.max(0, stock);

  const brandName = product.brandName ?? product.brand ?? product.brand_title ?? null;
  const categoryId =
    product.category_id ??
    product.categoryId ??
    product.category ??
    product.mainCategoryId ??
    product.categoryCode ??
    product.categoryName ??
    'uncategorized';
  const subcategory =
    product.subCategory ??
    product.subCategoryName ??
    product.subCategoryId ??
    product.categoryLevel2 ??
    product.childCategory ??
    null;

  const description = product.product_desc ?? product.productDesc ?? product.description ?? product.details ?? '';

  const featureList = ensureArray(product.features ?? product.featureList ?? product.highlightPoints).flatMap((feature) =>
    typeof feature === 'string'
      ? feature.split(/[\r\n]+/).map((item) => item.trim()).filter(Boolean)
      : feature?.text
      ? [feature.text]
      : feature
      ? [String(feature)]
      : [],
  );

  const specifications = ensureArray(product.specifications ?? product.specificationList ?? product.specs)
    .map(mapSpecification)
    .filter(Boolean);

  const badges = [];
  if (product.isHot || product.badgeHot || product.trending) badges.push('hot');
  if (product.isNew || product.badgeNew || product.newArrival) badges.push('new');
  if (product.isOnSale || product.badgeSale || product.discount > 0 || product.deal || product.promo_price) {
    badges.push('sale');
  }

  return {
    id: productId ?? slug ?? name,
    sku: product.sku ?? product.productSku ?? product.code ?? productId ?? slug ?? name,
    name,
    slug,
    category: categoryId ?? 'uncategorized',
    subcategory,
    brand: brandName,
    price,
    originalPrice,
    image: primaryImage || PLACEHOLDER_IMAGE,
    images: gallery.length > 0 ? gallery : [primaryImage || PLACEHOLDER_IMAGE],
    rating,
    reviews,
    stock,
    badges,
    description,
    specifications,
    features: featureList,
    raw: product,
  };
};

const mapCategory = (category) => {
  if (!category) return null;
  const name = category.name ?? category.categoryName ?? category.title ?? 'Category';
  const categoryId = category.categoryId ?? category.id ?? category.code ?? slugify(name);
  const image =
    category.categoryImage ??
    category.image ??
    category.bannerImage ??
    category.thumbnail ??
    PLACEHOLDER_IMAGE;

  const subCategoriesSource = category.subCategories ?? category.children ?? category.childCategories ?? [];
  const subCategories = ensureArray(subCategoriesSource).map((sub) => {
    if (!sub) return null;
    if (typeof sub === 'string') return sub;
    return sub.name ?? sub.categoryName ?? sub.title ?? null;
  }).filter(Boolean);

  return {
    id: categoryId,
    name,
    slug: category.slug ?? category.categorySlug ?? slugify(name),
    image,
    subcategories: subCategories,
    raw: category,
  };
};

const mapBrand = (brand) => {
  if (!brand) return null;
  const name = brand.brandName ?? brand.name ?? brand.title ?? '';
  const brandId = brand.brandId ?? brand.id ?? brand.code ?? slugify(name);
  const logo =
    brand.brandImage ??
    brand.logo ??
    brand.image ??
    PLACEHOLDER_IMAGE;

  return {
    id: brandId,
    name: name || 'Brand',
    slug: brand.slug ?? brand.brandSlug ?? slugify(name || brandId),
    image: logo,
    raw: brand,
  };
};

export const fetchProducts = async () => {
  const customerViewId = getCustomerViewId();
  const priceConfigId = getPriceConfigId();
  
  // Use the customer view specific endpoint if customerViewId is available
  if (customerViewId) {
    try {
      const params = priceConfigId ? { priceConfigId } : {};
      const response = await apiClient.get(`/usermgmt/getAllProductsByCustomerViewId/${customerViewId}`, { params });
      return normalizeArrayResponse(response).map(mapProduct).filter(Boolean);
    } catch (error) {
      console.error('Error fetching products by customer view ID:', error);
      // Fallback to default endpoint if customer view endpoint fails
      const response = await apiClient.get('/usermgmt/getAllProductDetails');
      return normalizeArrayResponse(response).map(mapProduct).filter(Boolean);
    }
  }
  
  // Fallback to default endpoint if no customerViewId is available
  const response = await apiClient.get('/usermgmt/getAllProductDetails');
  return normalizeArrayResponse(response).map(mapProduct).filter(Boolean);
};

export const fetchProductById = async (productId) => {
  if (!productId) return null;
  try {
    const customerViewId = getCustomerViewId();
    const params = customerViewId ? { customerViewId } : {};
    const response = await apiClient.get(`/usermgmt/getProductById/${productId}`, { params });
    const payload = normalizeObjectResponse(response);
    const productCandidate = payload.data ?? payload.product ?? payload;
    return mapProduct(productCandidate);
  } catch (error) {
    return null;
  }
};

export const fetchProductDetail = async (identifier) => {
  if (!identifier) return null;
  try {
    const customerViewId = getCustomerViewId();
    const params = customerViewId ? { customerViewId } : {};
    const response = await apiClient.get(`/usermgmt/getProductDetail/${identifier}`, { params });
    const payload = normalizeObjectResponse(response);
    const productCandidate = payload.data ?? payload.product ?? payload;
    return mapProduct(productCandidate);
  } catch (error) {
    return null;
  }
};

// Fetch product specifically through the customer view endpoint
export const fetchProductFromCustomerView = async (productId) => {
  if (!productId) return null;
  try {
    const customerViewId = getCustomerViewId();
    if (!customerViewId) {
      // If customer view id is not available, don't attempt this endpoint
      return null;
    }

    const priceConfigId = getPriceConfigId();
    const params = priceConfigId ? { priceConfigId } : {};

    const response = await apiClient.get(`/usermgmt/getProductFromCustomerView/${customerViewId}/${productId}`, { params });
    const payload = normalizeObjectResponse(response);
    const productCandidate = payload.data ?? payload.product ?? payload;
    const mapped = mapProduct(productCandidate);
    // Preserve raw stock details if present for warehouse selection
    return {
      ...mapped,
      stockDetails: productCandidate?.stock ?? productCandidate?.stockDetails ?? []
    };
  } catch (error) {
    console.error('Error fetching product from customer view:', error);
    return null;
  }
};

export const fetchBrands = async () => {
  const customerViewId = getCustomerViewId();
  const params = customerViewId ? { customerViewId } : {};
  const response = await apiClient.get('/usermgmt/allBrand', { params });
  return normalizeArrayResponse(response).map(mapBrand).filter(Boolean);
};

export const fetchCategories = async () => {
  const customerViewId = getCustomerViewId();
  const params = customerViewId ? { customerViewId } : {};
  const response = await apiClient.get('/usermgmt/getAllCategories', { params });
  return normalizeArrayResponse(response).map(mapCategory).filter(Boolean);
};

export const fetchCategoriesTree = async () => {
  try {
    const customerViewId = getCustomerViewId();
    const params = customerViewId ? { customerViewId } : {};
    const response = await apiClient.get('/usermgmt/getAllCategoriesTree', { params });
    return normalizeArrayResponse(response);
  } catch (error) {
    console.error('Error fetching categories tree:', error);
    return [];
  }
};
